import type {
  SiteBuilderDesign,
} from "@webdo/site-templates/builder";

export const BUILDER_INLINE_TEXT_MIXED_VALUE = "__mixed__";

export const BUILDER_INLINE_TEXT_STYLE_KEYS = [
  "fontFamily",
  "fontSize",
  "fontStyle",
  "fontWeight",
  "lineHeight",
  "textAlign",
  "textColor",
  "textDecoration",
] as const;

export type BuilderInlineTextStyleKey =
  typeof BUILDER_INLINE_TEXT_STYLE_KEYS[number];

type BuilderComputedInlineTextStyle = Pick<
  CSSStyleDeclaration,
  | "color"
  | "direction"
  | "fontFamily"
  | "fontSize"
  | "fontStyle"
  | "fontWeight"
  | "lineHeight"
  | "textAlign"
  | "textDecorationLine"
>;

export const BUILDER_INLINE_TEXT_STYLE_PRESETS = [
  {
    id: "paragraph",
    values: {
      fontSize: "16px",
      fontWeight: "400",
      lineHeight: "1.65",
    },
  },
  {
    id: "lead",
    values: {
      fontSize: "18px",
      fontWeight: "500",
      lineHeight: "1.65",
    },
  },
  {
    id: "heading",
    values: {
      fontSize: "40px",
      fontWeight: "800",
      lineHeight: "1.08",
    },
  },
  {
    id: "subheading",
    values: {
      fontSize: "28px",
      fontWeight: "800",
      lineHeight: "1.16",
    },
  },
  {
    id: "small",
    values: {
      fontSize: "13px",
      fontWeight: "500",
      lineHeight: "1.45",
    },
  },
] as const;

export type BuilderInlineTextStylePresetId =
  typeof BUILDER_INLINE_TEXT_STYLE_PRESETS[number]["id"];

export const BUILDER_INLINE_TEXT_FONT_WEIGHTS = [
  "300",
  "400",
  "500",
  "600",
  "700",
  "800",
  "900",
] as const;

export const BUILDER_INLINE_TEXT_COLOR_SWATCHES = [
  { id: "black", value: "#111827" },
  { id: "muted", value: "#64748b" },
  { id: "primary", value: "var(--site-primary)" },
  { id: "white", value: "#ffffff" },
] as const;

export type BuilderInlineTextColorSwatchId =
  typeof BUILDER_INLINE_TEXT_COLOR_SWATCHES[number]["id"];

function styleString(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }

  return typeof value === "number" && Number.isFinite(value)
    ? String(value)
    : "";
}

export function builderInlineTextStylesFromComputedStyle(
  style: BuilderComputedInlineTextStyle,
): Record<BuilderInlineTextStyleKey, string> {
  const fontFamily = style.fontFamily
    .split(",")[0]
    ?.trim()
    .replace(/^(['"])(.*)\1$/, "$2") ?? style.fontFamily.trim();
  const fontWeight = style.fontWeight.trim() === "bold"
    ? "700"
    : style.fontWeight.trim() === "normal"
      ? "400"
      : style.fontWeight.trim();
  const textAlign = style.textAlign.trim() === "start"
    ? style.direction === "rtl" ? "right" : "left"
    : style.textAlign.trim() === "end"
      ? style.direction === "rtl" ? "left" : "right"
      : style.textAlign.trim();

  return {
    fontFamily,
    fontSize: style.fontSize.trim(),
    fontStyle: style.fontStyle.trim() || "normal",
    fontWeight,
    lineHeight: style.lineHeight.trim(),
    textAlign,
    textColor: style.color.trim(),
    textDecoration: style.textDecorationLine.trim() || "none",
  };
}

function comparablePresetValue(
  values: Readonly<Record<string, unknown>>,
  mixed: ReadonlySet<string>,
  key: BuilderInlineTextStyleKey,
): string {
  if (mixed.has(key)) {
    return BUILDER_INLINE_TEXT_MIXED_VALUE;
  }

  const value = styleString(values[key]);

  if (key === "fontWeight") {
    if (value === "bold") return "700";
    if (value === "normal") return "400";
  }

  if (key === "lineHeight" && value.endsWith("px")) {
    const lineHeight = Number.parseFloat(value);
    const fontSize = mixed.has("fontSize")
      ? Number.NaN
      : Number.parseFloat(styleString(values.fontSize));

    if (
      Number.isFinite(lineHeight)
      && Number.isFinite(fontSize)
      && fontSize > 0
    ) {
      return String(Math.round((lineHeight / fontSize) * 100) / 100);
    }
  }

  return value;
}

export function builderInlineTextPresetForStyles(
  values: Readonly<Record<string, unknown>>,
  mixed: ReadonlySet<string> = new Set(),
): BuilderInlineTextStylePresetId | "" {
  return BUILDER_INLINE_TEXT_STYLE_PRESETS.find((preset) =>
    Object.entries(preset.values).every(([rawKey, expected]) => (
      comparablePresetValue(
        values,
        mixed,
        rawKey as BuilderInlineTextStyleKey,
      ) === expected
    ))
  )?.id ?? "";
}

export function builderInlineTextPresetSelectValue(
  preset: BuilderInlineTextStylePresetId | "",
  mixed: ReadonlySet<string>,
): BuilderInlineTextStylePresetId | "" | typeof BUILDER_INLINE_TEXT_MIXED_VALUE {
  return mixed.size > 0 && preset === ""
    ? BUILDER_INLINE_TEXT_MIXED_VALUE
    : preset;
}

export function builderInlineTextSelectValue(
  value: unknown,
  mixed: boolean,
): string {
  return mixed
    ? BUILDER_INLINE_TEXT_MIXED_VALUE
    : styleString(value);
}

export function builderInlineTextFontFamilies(
  design: SiteBuilderDesign,
  currentValue: unknown,
  mixed: boolean,
): string[] {
  return Array.from(new Set([
    design.typography?.heading,
    design.typography?.body,
    "Inter",
    "Georgia",
    "Arial",
    mixed ? "" : styleString(currentValue),
  ].filter((value): value is string => (
    typeof value === "string" && value.trim() !== ""
  ))));
}

export function builderInlineTextPixelValue(
  value: unknown,
  mixed: boolean,
): string {
  if (mixed) {
    return "";
  }

  const parsed = Number.parseFloat(styleString(value));

  return Number.isFinite(parsed)
    ? String(Math.round(parsed * 100) / 100)
    : "";
}

export function builderInlineTextBoldActive(
  value: unknown,
  mixed: boolean,
): boolean {
  if (mixed) {
    return false;
  }

  const normalized = styleString(value);

  return normalized === "bold"
    || Number.parseInt(normalized, 10) >= 700;
}

export function builderInlineTextItalicActive(
  value: unknown,
  mixed: boolean,
): boolean {
  return !mixed && value === "italic";
}

export function builderInlineTextUnderlineActive(
  value: unknown,
  mixed: boolean,
): boolean {
  return !mixed && styleString(value).includes("underline");
}

export function builderInlineTextAlignment(
  value: unknown,
  mixed: boolean,
): string {
  return mixed
    ? BUILDER_INLINE_TEXT_MIXED_VALUE
    : styleString(value) || "left";
}

export function builderInlineTextColorInputValue(
  value: unknown,
  mixed: boolean,
): string {
  if (mixed) {
    return "#000000";
  }

  const normalized = styleString(value);
  const hex = normalized.match(/^#([0-9a-f]{6})$/i);

  if (hex) {
    return `#${hex[1]}`;
  }

  const rgb = normalized.match(
    /^rgba?\(\s*(\d+)\s*[, ]\s*(\d+)\s*[, ]\s*(\d+)/i,
  );

  if (!rgb) {
    return "#000000";
  }

  return `#${[rgb[1], rgb[2], rgb[3]]
    .map((part) => (
      Math.max(0, Math.min(255, Number(part)))
        .toString(16)
        .padStart(2, "0")
    ))
    .join("")}`;
}

export function builderInlineTextColorSwatchActive(
  value: unknown,
  mixed: boolean,
  swatch: typeof BUILDER_INLINE_TEXT_COLOR_SWATCHES[number],
): boolean {
  return !mixed
    && swatch.value !== "var(--site-primary)"
    && builderInlineTextColorInputValue(value, false) === swatch.value;
}

export function normalizeBuilderInlineTextPixelDraft(
  draft: string,
  min: number,
  max: number,
): string | null {
  const parsed = Number.parseFloat(draft.trim());

  if (!Number.isFinite(parsed)) {
    return null;
  }

  return String(
    Math.round(Math.max(min, Math.min(max, parsed)) * 100) / 100,
  );
}
